import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { ApiRoutes } from './ApiRoutes';
import { Gt2ApiService } from './gt2-api.service';
import {
  ProductQuestionPayloadV3,
  ProductQuestionV3,
  UpsellForProductPayloadV3,
  UpsellPayloadV3,
  UpsellV3,
} from './vente-incitative-v3.model';

/**
 * Client de la vente incitative V3 (containers ShopProductQuestion + ShopUpsell) :
 * /v1/shops/{shopUuid}/product-questions|upsells et /v1/product-questions/*,
 * /v1/upsells/*. Auth / tenant / langue posés par les intercepteurs globaux.
 * UUID-only. Les endpoints exigent MODULE_CEL_PRODUCT_QUESTIONS (questions) ou
 * MODULE_CEL_UPSALE (upsells) côté API.
 */
@Injectable({
  providedIn: 'root',
})
export class VenteIncitativeV3Service {
  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private api: Gt2ApiService,
  ) {}

  // ----o Questions produit (ShopProductQuestion)

  public getProductQuestions(
    shopUuid: string,
  ): Observable<{ data: ProductQuestionV3[] }> {
    return this.http.get<{ data: ProductQuestionV3[] }>(
      this.api.createUrl(ApiRoutes.SHOP) + shopUuid + '/product-questions',
    );
  }

  public createProductQuestion(
    shopUuid: string,
    payload: ProductQuestionPayloadV3,
  ): Observable<{ data: ProductQuestionV3 }> {
    return this.http.post<{ data: ProductQuestionV3 }>(
      this.api.createUrl(ApiRoutes.SHOP) + shopUuid + '/product-questions',
      payload,
    );
  }

  public updateProductQuestion(
    questionUuid: string,
    payload: ProductQuestionPayloadV3,
  ): Observable<{ data: ProductQuestionV3 }> {
    return this.http.patch<{ data: ProductQuestionV3 }>(
      this.api.createUrl(ApiRoutes.PRODUCT_QUESTION) + questionUuid,
      payload,
    );
  }

  public deleteProductQuestion(questionUuid: string): Observable<any> {
    return this.http.delete<any>(
      this.api.createUrl(ApiRoutes.PRODUCT_QUESTION) + questionUuid,
    );
  }

  public moveProductQuestionBefore(
    movedUuid: string,
    targetUuid: string,
  ): Observable<any> {
    return this.http.post<any>(
      this.api.createUrl(ApiRoutes.PRODUCT_QUESTION) + 'move-before',
      { uuid_moved: movedUuid, uuid_target: targetUuid },
    );
  }

  public moveProductQuestionAfter(
    movedUuid: string,
    targetUuid: string,
  ): Observable<any> {
    return this.http.post<any>(
      this.api.createUrl(ApiRoutes.PRODUCT_QUESTION) + 'move-after',
      { uuid_moved: movedUuid, uuid_target: targetUuid },
    );
  }

  public attachProductToQuestion(
    questionUuid: string,
    productUuid: string,
  ): Observable<{ data: ProductQuestionV3 }> {
    return this.http.post<{ data: ProductQuestionV3 }>(
      this.api.createUrl(ApiRoutes.PRODUCT_QUESTION) +
        questionUuid +
        '/products',
      { product_uuid: productUuid },
    );
  }

  public detachProductFromQuestion(
    questionUuid: string,
    productUuid: string,
  ): Observable<{ data: ProductQuestionV3 }> {
    return this.http.delete<{ data: ProductQuestionV3 }>(
      this.api.createUrl(ApiRoutes.PRODUCT_QUESTION) +
        questionUuid +
        '/products/' +
        productUuid,
    );
  }

  public duplicateProductQuestions(
    sourceShopUuid: string,
    targetShopUuid: string,
  ): Observable<{ data: ProductQuestionV3[] }> {
    return this.http.post<{ data: ProductQuestionV3[] }>(
      this.api.createUrl(ApiRoutes.SHOP) +
        sourceShopUuid +
        '/product-questions/duplicate',
      { target_shop_uuid: targetShopUuid },
    );
  }

  // ----o Upsells (ShopUpsell)

  public getShopUpsells(shopUuid: string): Observable<{ data: UpsellV3[] }> {
    return this.http.get<{ data: UpsellV3[] }>(
      this.api.createUrl(ApiRoutes.SHOP) + shopUuid + '/upsells',
    );
  }

  public createShopUpsell(
    shopUuid: string,
    payload: UpsellPayloadV3,
  ): Observable<{ data: UpsellV3 }> {
    return this.http.post<{ data: UpsellV3 }>(
      this.api.createUrl(ApiRoutes.SHOP) + shopUuid + '/upsells',
      payload,
    );
  }

  public updateShopUpsell(
    upsellUuid: string,
    payload: UpsellPayloadV3,
  ): Observable<{ data: UpsellV3 }> {
    return this.http.patch<{ data: UpsellV3 }>(
      this.api.createUrl(ApiRoutes.UPSELL) + upsellUuid,
      payload,
    );
  }

  public deleteShopUpsell(upsellUuid: string): Observable<any> {
    return this.http.delete<any>(
      this.api.createUrl(ApiRoutes.UPSELL) + upsellUuid,
    );
  }

  public moveShopUpsellBefore(
    movedUuid: string,
    targetUuid: string,
  ): Observable<any> {
    return this.http.post<any>(
      this.api.createUrl(ApiRoutes.UPSELL) + 'move-before',
      { uuid_moved: movedUuid, uuid_target: targetUuid },
    );
  }

  public moveShopUpsellAfter(
    movedUuid: string,
    targetUuid: string,
  ): Observable<any> {
    return this.http.post<any>(
      this.api.createUrl(ApiRoutes.UPSELL) + 'move-after',
      { uuid_moved: movedUuid, uuid_target: targetUuid },
    );
  }

  public attachProductToUpsell(
    upsellUuid: string,
    productUuid: string,
  ): Observable<{ data: UpsellV3 }> {
    return this.http.post<{ data: UpsellV3 }>(
      this.api.createUrl(ApiRoutes.UPSELL) + upsellUuid + '/products',
      { product_uuid: productUuid },
    );
  }

  public detachProductFromUpsell(
    upsellUuid: string,
    productUuid: string,
  ): Observable<{ data: UpsellV3 }> {
    return this.http.delete<{ data: UpsellV3 }>(
      this.api.createUrl(ApiRoutes.UPSELL) +
        upsellUuid +
        '/products/' +
        productUuid,
    );
  }

  /**
   * Panel fiche produit (1-à-N) : l'upsell dont ce produit est déclencheur
   * dans cette boutique. data:null = pas encore configuré (état normal).
   */
  public getUpsellForProduct(
    shopUuid: string,
    productUuid: string,
  ): Observable<{ data: UpsellV3 | null }> {
    return this.http.get<{ data: UpsellV3 | null }>(
      this.api.createUrl(ApiRoutes.SHOP) +
        shopUuid +
        '/products/' +
        productUuid +
        '/upsell',
    );
  }

  /** Find-or-create + full-replace des suggestions (voir UpsellForProductPayloadV3). */
  public upsertUpsellForProduct(
    shopUuid: string,
    productUuid: string,
    payload: UpsellForProductPayloadV3,
  ): Observable<{ data: UpsellV3 }> {
    return this.http.put<{ data: UpsellV3 }>(
      this.api.createUrl(ApiRoutes.SHOP) +
        shopUuid +
        '/products/' +
        productUuid +
        '/upsell',
      payload,
    );
  }

  public duplicateShopUpsells(
    sourceShopUuid: string,
    targetShopUuid: string,
  ): Observable<{ data: UpsellV3[] }> {
    return this.http.post<{ data: UpsellV3[] }>(
      this.api.createUrl(ApiRoutes.SHOP) +
        sourceShopUuid +
        '/upsells/duplicate',
      { target_shop_uuid: targetShopUuid },
    );
  }
}
